/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Box, Button, FlexColumn, Text } from '@/shared/ui';
import { clearPersistedOnboarding } from '@features/onboarding/hooks/usePersistedOnboarding';
import LogoRingSpinner from '@/shared/ui/components/basic/loading/LogoRingSpinner';
import type { ActivationStatus } from '../hooks/useSubscriptionActivation';

interface SubscriptionActivationScreenProps {
  status: Exclude<ActivationStatus, 'SUCCESS'>;
  agencyId: string;
  onRetry: () => void;
}

const ACTIVATION_TITLE_ID = 'subscription-activation-title';

/**
 * Loading state shown while the payment confirmation is still in flight.
 *
 * @returns The activation loading spinner with reassuring copy.
 */
function LoadingState() {
  const { t } = useTranslation('features/subscription');
  return <LogoRingSpinner text={t('activation.loading')} />;
}

/**
 * Timeout state shown when confirmation takes longer than expected, offering
 * retry and workspace-return actions.
 *
 * @param onRetry - Callback invoked when the user retries confirmation.
 * @returns The activation timeout panel.
 */
function TimeoutState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('features/subscription');
  const navigate = useNavigate();
  return (
    <FlexColumn className="gap-(--space-4) items-center text-center">
      <Text id={ACTIVATION_TITLE_ID} as="h2" className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
        {t('activation.timeoutTitle')}
      </Text>
      <Text className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {t('activation.timeoutBody')}
      </Text>
      <Button
        fullWidth
        onClick={onRetry}
        className="min-h-[48px] rounded-(--radius-md) font-semibold bg-[var(--button-primary-bg)] hover:bg-[var(--button-primary-hover-bg)] text-[var(--button-primary-text)]"
      >
        {t('activation.retry')}
      </Button>
      <Button variant="link" size="sm" onClick={() => { clearPersistedOnboarding(); navigate('/app/workspace'); }} style={{ fontSize: '0.8125rem', fontWeight: 500 }}>
        {t('activation.backToWorkspace')}
      </Button>
    </FlexColumn>
  );
}

/**
 * Post-checkout confirmation screen shown while the subscription is being
 * reconciled with Payien. Replaces the misleading "Assinatura necessária"
 * overlay during the activation race window.
 *
 * @param status - Current activation status (LOADING or TIMEOUT).
 * @param onRetry - Callback invoked when the user retries confirmation.
 * @returns The full-screen activation dialog.
 */
export function SubscriptionActivationScreen({ status, onRetry }: SubscriptionActivationScreenProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={ACTIVATION_TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'var(--surface-primary)' }}
    >
      <Box className="flex flex-col gap-(--space-5) items-center text-center p-(--space-8) mx-(--space-4) w-full max-w-md sm:mx-0">
        {status === 'LOADING' ? <LoadingState /> : <TimeoutState onRetry={onRetry} />}
      </Box>
    </div>
  );
}
